<template>
  <div class="insurances">
    <header class="section-header">
      <div class="header-text">
        <h1>Seguros</h1>
        <p class="muted">
          Gestiona tus pólizas, vencimientos y documentos asociados.
        </p>
        <div class="header-meta">
          <span class="chip ghost">Total: {{ visibleCount }}</span>
          <span class="chip success">Activos: {{ activeCount }}</span>
          <span v-if="expiringCount" class="chip warning">
            Caducan pronto: {{ expiringCount }}
          </span>
        </div>
      </div>
      <button class="primary" @click="openModal()">+ Nuevo seguro</button>
    </header>

    <section class="filters">
      <div class="filter-group">
        <label for="insurance-search">Buscar</label>
        <input
          id="insurance-search"
          v-model="searchQuery"
          type="text"
          placeholder="Nombre, proveedor, póliza..."
        />
      </div>
      <div class="filter-group">
        <label>Estado</label>
        <div class="filter-chips">
          <button
            class="chip"
            :class="{ active: activeFilter === 'active' }"
            @click="setFilter('active')"
          >
            Activos
          </button>
          <button
            class="chip"
            :class="{ active: activeFilter === 'expiring' }"
            @click="setFilter('expiring')"
          >
            Caducan pronto
          </button>
        </div>
      </div>
      <div class="filter-group">
        <label for="insurance-category">Categoría</label>
        <select id="insurance-category" v-model="categoryFilter">
          <option value="">Todas las categorías</option>
          <option value="CAR">Coche</option>
          <option value="HOME">Hogar</option>
          <option value="HEALTH">Salud</option>
          <option value="LIFE">Vida</option>
          <option value="OTHER">Otros</option>
        </select>
      </div>
    </section>

    <section v-if="loading" class="state">Cargando seguros...</section>
    <section v-if="error" class="state error">{{ error }}</section>

    <section v-if="!loading" class="records">
      <div v-for="policy in filteredPolicies" :key="policy.id" class="record-card">
        <div class="record-header">
          <div class="record-title">
            <h3>{{ policy.name }}</h3>
            <p class="muted">
              {{ categoryLabel(policy.category) }} ·
              {{ policy.provider?.name || "Sin proveedor" }}
            </p>
            <p class="muted small">
              Renovación: {{ formatDate(policy.renewalDate) }}
            </p>
          </div>
          <div class="record-meta">
            <span class="chip">{{ formatEur(policy.priceAmount) }}</span>
            <span class="chip ghost">{{ paymentLabel(policy.paymentFrequency) }}</span>
            <span class="chip" :class="statusClass(policy.status)">
              {{ statusLabel(policy.status) }}
            </span>
          </div>
        </div>
        <p v-if="policy.policyNumber" class="muted small">
          Póliza: {{ policy.policyNumber }}
        </p>
        <div v-if="isExpiringSoon(policy)" class="alert">
          Renovación en {{ daysUntil(policy.renewalDate) }} días
        </div>
        <div class="record-actions">
          <button class="ghost" @click="goDetail(policy)">Ver</button>
          <button class="ghost" @click="openModal(policy)">Editar</button>
          <button class="ghost danger" @click="removePolicy(policy)">Eliminar</button>
        </div>
      </div>
      <div v-if="!filteredPolicies.length" class="empty-card">
        <p>No hay seguros registrados todavía.</p>
        <button class="ghost" @click="openModal()">Crear el primero</button>
      </div>
    </section>

    <div v-if="showModal" class="modal-backdrop" @click.self="closeModal">
      <div class="modal">
        <header>
          <h3>{{ form.id ? "Editar seguro" : "Nuevo seguro" }}</h3>
          <button class="icon danger" @click="closeModal">×</button>
        </header>
        <div class="modal-body">
          <label>
            Nombre
            <input v-model="form.name" type="text" placeholder="Seguro coche, hogar..." />
          </label>
          <div class="row">
            <label>
              Categoría
              <select v-model="form.category">
                <option value="">Selecciona</option>
                <option value="CAR">Coche</option>
                <option value="HOME">Hogar</option>
                <option value="HEALTH">Salud</option>
                <option value="LIFE">Vida</option>
                <option value="OTHER">Otros</option>
              </select>
            </label>
            <label>
              Aseguradora
              <select v-model="form.providerId">
                <option value="">Selecciona</option>
                <option v-for="provider in providers" :key="provider.id" :value="provider.id">
                  {{ provider.name }}
                </option>
              </select>
            </label>
          </div>
          <div class="row">
            <label>
              Nº póliza
              <input v-model="form.policyNumber" type="text" placeholder="Opcional" />
            </label>
            <label>
              Estado
              <select v-model="form.status">
                <option value="ACTIVE">Activo</option>
                <option value="CANCELLED">Cancelado</option>
                <option value="EXPIRED">Caducado</option>
              </select>
            </label>
          </div>
          <div class="row">
            <label>
              Importe
              <input v-model.number="form.priceAmount" type="number" min="0" step="0.01" />
            </label>
            <label>
              Frecuencia
              <select v-model="form.paymentFrequency">
                <option value="MONTHLY">Mensual</option>
                <option value="QUARTERLY">Trimestral</option>
                <option value="YEARLY">Anual</option>
                <option value="ONE_TIME">Pago único</option>
              </select>
            </label>
          </div>
          <label>
            Método de pago
            <input v-model="form.paymentMethod" type="text" placeholder="Tarjeta, domiciliación..." />
          </label>
          <div class="row">
            <label>
              Inicio
              <input v-model="form.startDate" type="date" />
            </label>
            <label>
              Renovación
              <input v-model="form.renewalDate" type="date" />
            </label>
          </div>
          <label>
            Avisar antes de
            <input v-model="form.cancelByDate" type="date" />
          </label>
          <div class="row">
            <label>
              Teléfono
              <input v-model="form.phone" type="text" />
            </label>
            <label>
              Email
              <input v-model="form.email" type="email" />
            </label>
          </div>
          <label>
            Notas
            <textarea v-model="form.notes" rows="3" placeholder="Notas internas"></textarea>
          </label>
          <p class="muted small">
            Al guardar, se creará automáticamente un gasto recurrente vinculado a esta póliza.
          </p>
          <p v-if="formError" class="state error">{{ formError }}</p>
        </div>
        <footer>
          <button class="ghost" @click="closeModal">Cancelar</button>
          <button class="primary" :disabled="saving" @click="savePolicy">
            {{ saving ? "Guardando..." : "Guardar" }}
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import {
  fetchInsurances,
  createInsurance,
  updateInsurance,
  deleteInsurance,
} from "../services/insurances";
import { fetchExpenseProviders } from "../services/expenses";

export default {
  name: "InsuranceView",
  data() {
    return {
      policies: [],
      providers: [],
      loading: false,
      error: "",
      showModal: false,
      saving: false,
      formError: "",
      form: this.emptyForm(),
      activeFilter: "active",
      categoryFilter: "",
      searchQuery: "",
      searchQueryDebounced: "",
      searchTimer: null,
    };
  },
  async mounted() {
    await this.loadProviders();
    await this.loadPolicies();
  },
  computed: {
    filteredPolicies() {
      const query = this.searchQueryDebounced.trim().toLowerCase();
      const list = [...(this.policies || [])];
      const filtered = query
        ? list.filter((policy) => this.matchesSearch(policy, query))
        : list;
      return filtered.sort((a, b) => {
        const aDate = a.renewalDate ? new Date(a.renewalDate).getTime() : Infinity;
        const bDate = b.renewalDate ? new Date(b.renewalDate).getTime() : Infinity;
        return aDate - bDate;
      });
    },
    visibleCount() {
      return this.filteredPolicies.length;
    },
    expiringCount() {
      return this.filteredPolicies.filter((policy) => this.isExpiringSoon(policy)).length;
    },
    activeCount() {
      return this.filteredPolicies.filter((policy) => policy.status === "ACTIVE").length;
    },
  },
  watch: {
    activeFilter() {
      this.loadPolicies();
    },
    categoryFilter() {
      this.loadPolicies();
    },
    searchQuery(value) {
      this.queueSearch(value);
    },
  },
  methods: {
    emptyForm() {
      return {
        id: null,
        name: "",
        category: "",
        providerId: "",
        policyNumber: "",
        status: "ACTIVE",
        priceAmount: "",
        paymentFrequency: "YEARLY",
        paymentMethod: "",
        startDate: "",
        renewalDate: "",
        cancelByDate: "",
        phone: "",
        email: "",
        notes: "",
      };
    },
    async loadProviders() {
      try {
        this.providers = await fetchExpenseProviders();
      } catch (_) {
        this.providers = [];
      }
    },
    buildParams() {
      const params = {};
      if (this.activeFilter === "active") params.status = "ACTIVE";
      if (this.activeFilter === "expiring") params.expiringSoon = true;
      if (this.categoryFilter) params.category = this.categoryFilter;
      return params;
    },
    async loadPolicies() {
      this.loading = true;
      this.error = "";
      try {
        const policies = await fetchInsurances(this.buildParams());
        this.policies = (policies || []).map((policy) => this.normalizePolicy(policy));
      } catch (err) {
        this.error = err?.message || "No se pudieron cargar los seguros.";
      } finally {
        this.loading = false;
      }
    },
    setFilter(filter) {
      this.activeFilter = filter;
    },
    queueSearch(value) {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }
      this.searchTimer = setTimeout(() => {
        this.searchQueryDebounced = value;
      }, 200);
    },
    matchesSearch(policy, query) {
      if (!query) return true;
      const fields = [
        policy.name,
        policy.policyNumber,
        policy.paymentMethod,
        policy.provider?.name,
        this.categoryLabel(policy.category),
      ];
      return fields
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query);
    },
    openModal(policy = null) {
      this.formError = "";
      if (policy) {
        const normalized = this.normalizePolicy(policy);
        this.form = {
          id: normalized.id,
          name: normalized.name || "",
          category: normalized.category || "",
          providerId: normalized.provider?.id || normalized.providerId || "",
          policyNumber: normalized.policyNumber || "",
          status: normalized.status || "ACTIVE",
          priceAmount: normalized.priceAmount || "",
          paymentFrequency: normalized.paymentFrequency || "YEARLY",
          paymentMethod: normalized.paymentMethod || "",
          startDate: normalized.startDate || "",
          renewalDate: normalized.renewalDate || "",
          cancelByDate: normalized.cancelByDate || "",
          phone: normalized.phone || "",
          email: normalized.email || "",
          notes: normalized.notes || "",
        };
      } else {
        this.form = this.emptyForm();
      }
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
    async savePolicy() {
      this.formError = "";
      if (!this.form.name || !this.form.providerId || !this.form.priceAmount || !this.form.renewalDate) {
        this.formError = "Nombre, proveedor, importe y renovación son obligatorios.";
        return;
      }
      if (Number(this.form.priceAmount) <= 0) {
        this.formError = "El importe debe ser mayor que 0.";
        return;
      }
      if (this.form.startDate && this.form.renewalDate < this.form.startDate) {
        this.formError = "La renovación no puede ser anterior al inicio.";
        return;
      }
      this.saving = true;
      try {
        const payload = {
          name: this.form.name,
          category: this.form.category || "OTHER",
          providerId: Number(this.form.providerId),
          policyNumber: this.form.policyNumber || null,
          status: this.form.status || "ACTIVE",
          priceAmount: Number(this.form.priceAmount),
          paymentFrequency: this.form.paymentFrequency || "YEARLY",
          paymentMethod: this.form.paymentMethod || null,
          startDate: this.form.startDate || null,
          renewalDate: this.form.renewalDate || null,
          cancelByDate: this.form.cancelByDate || null,
          phone: this.form.phone || null,
          email: this.form.email || null,
          notes: this.form.notes || null,
        };
        if (this.form.id) {
          await updateInsurance(this.form.id, payload);
        } else {
          await createInsurance(payload);
        }
        this.closeModal();
        await this.loadPolicies();
      } catch (err) {
        this.formError = err?.message || "No se pudo guardar.";
      } finally {
        this.saving = false;
      }
    },
    async removePolicy(policy) {
      if (!confirm(`Eliminar ${policy.name}?`)) return;
      try {
        await deleteInsurance(policy.id);
        await this.loadPolicies();
      } catch (err) {
        this.error = err?.message || "No se pudo eliminar.";
      }
    },
    goDetail(policy) {
      this.$router.push(`/dashboard/insurances/${policy.id}`);
    },
    formatDate(value) {
      if (!value) return "-";
      return new Date(value).toLocaleDateString();
    },
    formatEur(amount) {
      if (amount === null || amount === undefined) return "-";
      return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(amount);
    },
    categoryLabel(value) {
      return {
        CAR: "Coche",
        HOME: "Hogar",
        HEALTH: "Salud",
        LIFE: "Vida",
        OTHER: "Otros",
      }[value] || "Otros";
    },
    paymentLabel(value) {
      return {
        MONTHLY: "Mensual",
        QUARTERLY: "Trimestral",
        YEARLY: "Anual",
        ONE_TIME: "Único",
      }[value] || "Anual";
    },
    statusLabel(value) {
      return {
        ACTIVE: "Activo",
        CANCELLED: "Cancelado",
        EXPIRED: "Caducado",
      }[value] || "Activo";
    },
    statusClass(value) {
      return {
        ACTIVE: "success",
        CANCELLED: "ghost",
        EXPIRED: "danger",
      }[value] || "ghost";
    },
    normalizePolicy(policy) {
      if (!policy) return policy;
      return {
        ...policy,
        policyNumber: policy.policyNumber ?? policy.policy_number ?? "",
        paymentFrequency: policy.paymentFrequency ?? policy.payment_frequency ?? "",
        paymentMethod: policy.paymentMethod ?? policy.payment_method ?? "",
        priceAmount: policy.priceAmount ?? policy.price_amount ?? "",
        startDate: policy.startDate ?? policy.start_date ?? "",
        renewalDate: policy.renewalDate ?? policy.renewal_date ?? "",
        cancelByDate: policy.cancelByDate ?? policy.cancel_by_date ?? "",
        providerId: policy.providerId ?? policy.provider_id ?? null,
      };
    },
    daysUntil(date) {
      if (!date) return 0;
      const diff = new Date(date).getTime() - Date.now();
      return Math.max(0, Math.ceil(diff / 86400000));
    },
    isExpiringSoon(policy) {
      return policy?.renewalDate && this.daysUntil(policy.renewalDate) <= 30;
    },
  },
};
</script>

<style scoped>
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 24px;
}

.muted {
  color: var(--muted);
  margin: 0;
}

.muted.small {
  font-size: 12px;
}

.header-text {
  display: grid;
  gap: 8px;
}

.header-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.filters {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  align-items: end;
  margin-bottom: 20px;
  background: rgba(12, 18, 32, 0.6);
  border: 1px solid var(--border);
  border-radius: 16px;
  padding: 12px;
  backdrop-filter: blur(10px);
}

.filter-group {
  display: grid;
  gap: 6px;
}

.filter-group label {
  font-size: 12px;
  color: var(--muted);
}

.filter-group input,
.filter-group select {
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 8px 10px;
  color: var(--text);
}

.filter-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.records {
  display: grid;
  gap: 16px;
}

.state {
  padding: 18px;
  text-align: center;
  color: var(--muted);
}

.state.error {
  color: var(--danger);
}

.record-card {
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 18px;
  padding: 18px;
  display: grid;
  gap: 12px;
  transition: border-color 0.2s ease, transform 0.2s ease;
}

.record-card:hover {
  border-color: rgba(56, 189, 248, 0.4);
  transform: translateY(-2px);
}

.record-header {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  align-items: flex-start;
  flex-wrap: wrap;
}

.record-title h3 {
  margin: 0 0 4px;
}

.record-meta {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.record-actions {
  display: flex;
  gap: 10px;
}

.alert {
  padding: 8px 12px;
  border-radius: 12px;
  background: rgba(251, 191, 36, 0.16);
  color: #fcd34d;
  border: 1px solid rgba(251, 191, 36, 0.4);
  font-weight: 600;
  display: inline-flex;
}

.chip {
  background: rgba(148, 163, 184, 0.12);
  border: 1px solid rgba(148, 163, 184, 0.25);
  color: var(--text);
  padding: 6px 12px;
  border-radius: 999px;
  cursor: default;
  font-weight: 600;
  font-size: 12px;
}

button.chip {
  cursor: pointer;
}

.chip.active {
  border-color: rgba(56, 189, 248, 0.6);
  background: rgba(56, 189, 248, 0.2);
}

.chip.ghost {
  background: rgba(148, 163, 184, 0.1);
  border-color: rgba(148, 163, 184, 0.2);
  color: var(--muted);
}

.chip.warning {
  background: rgba(251, 191, 36, 0.16);
  border-color: rgba(251, 191, 36, 0.45);
  color: #fcd34d;
}

.chip.success {
  background: rgba(34, 197, 94, 0.18);
  color: #22c55e;
}

.chip.danger {
  background: rgba(248, 113, 113, 0.2);
  color: #f87171;
}

.ghost {
  background: rgba(148, 163, 184, 0.12);
  border: 1px solid rgba(148, 163, 184, 0.25);
  color: var(--text);
  padding: 8px 14px;
  border-radius: 999px;
  cursor: pointer;
  font-weight: 600;
}

.ghost.danger {
  color: var(--danger);
}

.primary {
  background: linear-gradient(120deg, var(--accent), var(--success));
  color: #071022;
  border: none;
  padding: 10px 18px;
  border-radius: 999px;
  font-weight: 700;
  cursor: pointer;
}

.empty-card {
  border: 1px dashed rgba(148, 163, 184, 0.3);
  border-radius: 16px;
  padding: 18px;
  text-align: center;
  color: var(--muted);
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(6, 10, 18, 0.65);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px 16px;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  z-index: 20;
}

.modal {
  width: min(620px, 100%);
  max-width: calc(100vw - 32px);
  background: var(--panel);
  border-radius: 20px;
  border: 1px solid var(--border);
  padding: 20px;
  display: grid;
  gap: 16px;
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  overflow-x: hidden;
}

.modal header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-body {
  display: grid;
  gap: 12px;
}

.modal-body label {
  display: grid;
  gap: 6px;
  color: var(--muted);
  font-size: 14px;
  min-width: 0;
}

.modal-body input,
.modal-body textarea,
.modal-body select {
  background: var(--panel-2);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 10px;
  color: var(--text);
  width: 100%;
  max-width: 100%;
}

.row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
}

.modal footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.icon {
  background: transparent;
  border: 1px solid var(--border);
  color: var(--muted);
  width: 28px;
  height: 28px;
  border-radius: 8px;
  cursor: pointer;
}

.icon.danger {
  color: var(--danger);
}

@media (max-width: 900px) {
  .section-header {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
